import { useEffect, useRef, useState } from 'react';
import { Card, SectionHeader } from '../school-settings-v2/components/ui';
import { useStaffSettingsState } from '../school-settings-v2/hooks/useStaffSettingsState';
import { StripePaymentMethodConfigurationCard } from '../school-settings-v2/components/StripePaymentMethodConfigurationCard';

const DEFAULT_BRAND_COLOR = '#2697a2';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const toBrandColor = (value: unknown): string => {
  const normalized = String(value ?? '').trim();
  return normalized || DEFAULT_BRAND_COLOR;
};

export const DcCleanupPage = () => {
  const { settingsState, persistPatch, isLoading, isSaving } = useStaffSettingsState();
  const [brandColor, setBrandColor] = useState(DEFAULT_BRAND_COLOR);
  const colorDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    setBrandColor(toBrandColor(asRecord(settingsState.organization.ext).brandColor));
  }, [settingsState.organization.ext]);

  useEffect(
    () => () => {
      if (colorDebounceRef.current !== null) {
        window.clearTimeout(colorDebounceRef.current);
      }
    },
    [],
  );

  const persistBrandColor = (value: string) => {
    void persistPatch((current) => ({
      ...current,
      organization: {
        ...current.organization,
        ext: {
          ...asRecord(current.organization.ext),
          brandColor: value,
        },
      },
    }));
  };

  const handleBrandColorChange = (value: string) => {
    setBrandColor(value);
    if (colorDebounceRef.current !== null) {
      window.clearTimeout(colorDebounceRef.current);
    }
    colorDebounceRef.current = window.setTimeout(() => {
      persistBrandColor(value);
    }, 500);
  };

  const handleBrandColorBlur = () => {
    if (colorDebounceRef.current !== null) {
      window.clearTimeout(colorDebounceRef.current);
      colorDebounceRef.current = null;
    }
    persistBrandColor(brandColor);
  };

  return (
    <div className="space-y-5" data-testid="super-administrator-control-plane-dc-cleanup">
      <Card>
        <SectionHeader
          title="Brand Color"
          description="Move temporary cleanup controls here while the school settings surface stays focused."
        />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <input
            type="color"
            value={brandColor}
            onChange={(event) => handleBrandColorChange(event.target.value)}
            onBlur={handleBrandColorBlur}
            className="h-10 w-16 rounded border border-border cursor-pointer bg-white p-0.5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Brand color picker"
            disabled={isLoading || isSaving}
            data-testid="dc-cleanup-brand-color-picker"
          />
          <div className="min-w-0">
            <p className="text-sm font-mono text-irongrey" data-testid="dc-cleanup-brand-color-value">
              {brandColor}
            </p>
            <p className="text-xs text-text-tertiary">500ms debounce</p>
          </div>
        </div>
      </Card>

      <StripePaymentMethodConfigurationCard canManageCatalog />
    </div>
  );
};
